var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import clsx from 'clsx';
import { FieldTitle, InputHelperText, sanitizeInputRestProps, useInput, } from 'react-admin';
import { isValid, parse } from 'date-fns';
import { TimePicker } from '@mui/x-date-pickers';
import { WithLocalizationProvider } from './WithLocalizationProvider';
/**
 * Form input to edit the Time of a Date string value or a Date object.
 * Renders a time picker from Mui X (https://mui.com/x/react-date-pickers/time-picker/).
 *
 * @example
 * import { Edit, SimpleForm } from 'react-admin';
 * import { TimeInput } from '@react-admin/ra-form-layout';
 *
 * const PostEdit = () => (
 *     <Edit>
 *         <SimpleForm>
 *             <TimeInput source="published_at" />
 *         </SimpleForm>
 *     </Edit>
 * );
 *
 * @example
 * // TimeInput return a Date object but you can pass a custom parse
 * // method to convert the form value to an ISO string.
 * <TimeInput source="published" parse={(date: Date) => (date ? date.toISOString() : null)} />
 */
export var TimeInput = function (props) {
    var className = props.className, _a = props.format, format = _a === void 0 ? defaultFormat : _a, mask = props.mask, label = props.label, source = props.source, resource = props.resource, helperText = props.helperText, fullWidth = props.fullWidth, variant = props.variant, slotProps = props.slotProps, rest = __rest(props, ["className", "format", "mask", "label", "source", "resource", "helperText", "fullWidth", "variant", "slotProps"]);
    var _b = useInput(__assign({ source: source, resource: resource, format: format }, rest)), field = _b.field, fieldState = _b.fieldState, formState = _b.formState, id = _b.id, isRequired = _b.isRequired;
    var error = fieldState.error, invalid = fieldState.invalid, isTouched = fieldState.isTouched;
    var isSubmitted = formState.isSubmitted;
    var renderHelperText = helperText !== false || ((isTouched || isSubmitted) && invalid);
    return (React.createElement(WithLocalizationProvider, null,
        React.createElement(TimePicker, __assign({ id: id, label: React.createElement(FieldTitle, { label: label, source: source, resource: resource, isRequired: isRequired }), slotProps: __assign(__assign({}, slotProps), { textField: __assign({ helperText: renderHelperText ? (React.createElement(InputHelperText, { touched: isTouched || isSubmitted, error: error === null || error === void 0 ? void 0 : error.message, helperText: helperText })) : null, fullWidth: fullWidth || false, error: (isTouched || isSubmitted) && invalid, variant: variant || 'filled', onBlur: field.onBlur }, slotProps === null || slotProps === void 0 ? void 0 : slotProps.textField) }), format: mask, onChange: function (date) {
                if (!isValid(date)) {
                    field.onChange(null);
                    return;
                }
                field.onChange(date);
            }, className: clsx('ra-input', "ra-input-".concat(source), className), value: field.value }, sanitizeInputRestProps(rest)))));
};
var defaultFormat = function (date) {
    if (!date)
        return null;
    var longLocalizedTime = parse(date, 'p', new Date());
    if (isValid(longLocalizedTime))
        return longLocalizedTime;
    var longLocalizedTimeWithSecs = parse(date, 'pp', new Date());
    if (isValid(longLocalizedTimeWithSecs))
        return longLocalizedTimeWithSecs;
    var parsedDate = new Date(date);
    return isValid(parsedDate) ? parsedDate : null;
};
